// The shared data-table look (Route Intelligence, AI Visibility) — a rounded frame, a
// tinted header band, striped rows with a neutral hover (brand-blue only on rows that
// open something), and roomier cells. Kept in one place so every table matches; the
// per-cell treatments (pills, inline bars) stay with the table that owns the data.
//
// The frame scrolls horizontally for wide tables and pairs with the existing
// `responsive-card-table` / `data-label` card layout below 500px (see index.css).

/** Scroll container + rounded border around a table. */
export const TABLE_FRAME = 'overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800';

/**
 * Scroll container for a table that sits flush inside a card (the card already draws
 * the border and rounded corners). A rounded frame here would curve the header band
 * and double the border.
 */
export const TABLE_FRAME_FLUSH = 'overflow-x-auto';

/** The header band. */
export const TABLE_THEAD = 'bg-gray-50 dark:bg-gray-800/60 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400';

/** Header cell padding (alignment is set per column). */
export const TABLE_TH = 'px-3 py-2.5 whitespace-nowrap';

/** A body row: divider, stripe and a neutral hover (a plain row does nothing on click). */
export const TABLE_ROW = 'border-t border-gray-100 dark:border-gray-800 transition-colors even:bg-gray-50/60 dark:even:bg-gray-800/20 hover:bg-gray-100/70 dark:hover:bg-gray-800/40';

/**
 * Extra classes for a row that opens something: pointer, brand-blue hover and a
 * visible keyboard focus ring. Pair with `clickableRowProps`.
 */
export const TABLE_ROW_CLICKABLE = 'cursor-pointer hover:!bg-[#32B1FF]/5 dark:hover:!bg-[#32B1FF]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#32B1FF]';

/**
 * Props that make a table row behave like a button: focusable, and Enter/Space do
 * what a click does. A bare <tr onClick> is mouse-only.
 * @param {() => void} onActivate
 */
export const clickableRowProps = (onActivate) => ({
  tabIndex: 0,
  onClick: onActivate,
  onKeyDown: (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  },
});

/** Extra classes for a row that is the current selection. */
export const TABLE_ROW_SELECTED = '!bg-[#32B1FF]/10 shadow-[inset_3px_0_0_#32B1FF]';

/** Body cell padding (alignment and colour are set per column). */
export const TABLE_TD = 'px-3 py-3';
